import { getSupabase } from './supabase';
import { getApiUrl } from './api';

export interface ReferralRewardSettings {
  referrer_reward: number;
  referred_reward: number;
  updated_at?: string;
  updated_by?: string;
}

export const DEFAULT_REFERRAL_SETTINGS: ReferralRewardSettings = {
  referrer_reward: 100,
  referred_reward: 50,
};

const REFERRAL_SETTINGS_CACHE_KEY = 'leton_referral_reward_settings_cache';

function parseRewardNumber(val: unknown): number | null {
  if (val === undefined || val === null || val === '') return null;
  if (typeof val === 'number') {
    return Number.isFinite(val) && val >= 0 ? Math.round(val) : null;
  }
  if (typeof val === 'string') {
    const trimmed = val.trim();
    if (!trimmed) return null;
    const num = Number(trimmed);
    return Number.isFinite(num) && num >= 0 ? Math.round(num) : null;
  }
  return null;
}

/**
 * Normalizes raw referral settings payload from Supabase leton_content or API.
 * Strictly maps referrer_reward -> pengundang and referred_reward -> teman yang diundang.
 * Returns null if neither field is present or valid.
 */
export function normalizeReferralRewardSettings(
  raw: unknown,
  rowUpdatedAt?: string
): ReferralRewardSettings | null {
  let c: any = raw;
  if (typeof c === 'string') {
    try {
      c = JSON.parse(c);
    } catch {
      return null;
    }
  }
  if (!c || typeof c !== 'object') {
    return null;
  }

  const referrerVal = parseRewardNumber(c.referrer_reward ?? c.referrerReward);
  const referredVal = parseRewardNumber(c.referred_reward ?? c.referredReward);

  if (referrerVal === null && referredVal === null) {
    return null;
  }

  return {
    referrer_reward: referrerVal !== null ? referrerVal : DEFAULT_REFERRAL_SETTINGS.referrer_reward,
    referred_reward: referredVal !== null ? referredVal : DEFAULT_REFERRAL_SETTINGS.referred_reward,
    updated_at: c.updated_at || c.updatedAt || rowUpdatedAt,
    updated_by: c.updated_by || c.updatedBy,
  };
}

function saveVerifiedCache(settings: ReferralRewardSettings): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(
      REFERRAL_SETTINGS_CACHE_KEY,
      JSON.stringify({
        ...settings,
        _verified: true,
      })
    );
  } catch {}
}

/**
 * Reads verified cached referral settings from localStorage.
 * Ignores stale/unverified cache entries that were previously written by server default fallbacks.
 */
export function getVerifiedCachedReferralSettings(): ReferralRewardSettings | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(REFERRAL_SETTINGS_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    // Require _verified flag or an authoritative updated_at timestamp to prevent stale 100/50 fallback cache
    if (parsed._verified !== true && !parsed.updated_at) {
      localStorage.removeItem(REFERRAL_SETTINGS_CACHE_KEY);
      return null;
    }
    return normalizeReferralRewardSettings(parsed);
  } catch {
    return null;
  }
}

/**
 * Fetch Referral Reward Settings.
 * 1. Primary (Authoritative Source of Truth): Direct Supabase read from public.leton_content (id = 'referral_settings').
 * 2. Secondary: Backend API /api/referral-settings (only if not a server default fallback).
 * 3. Tertiary: Verified local cache in localStorage.
 */
export async function getReferralRewardSettings(): Promise<{
  settings: ReferralRewardSettings;
  isFallback: boolean;
  isVerified: boolean;
}> {
  // 1. Primary: Direct Supabase leton_content read (Authoritative Source of Truth shared by Admin & Customer)
  try {
    const client = getSupabase();
    if (client && typeof client.from === 'function') {
      const { data: contentRow, error: contentErr } = await client
        .from('leton_content')
        .select('*')
        .eq('id', 'referral_settings')
        .maybeSingle();

      if (!contentErr && contentRow && contentRow.content !== undefined && contentRow.content !== null) {
        const normalized = normalizeReferralRewardSettings(contentRow.content, contentRow.updated_at);
        if (normalized) {
          saveVerifiedCache(normalized);
          return { settings: normalized, isFallback: false, isVerified: true };
        }
      }
    }
  } catch (err) {
    console.warn('[ReferralSettings] Supabase direct read warning:', err);
  }

  // 2. Secondary: Backend API /api/referral-settings (only accept if not an unconfigured server fallback)
  try {
    const res = await fetch(getApiUrl('/api/referral-settings'), {
      cache: 'no-store',
    });
    if (res.ok) {
      const json = await res.json();
      if (json && json.settings && json.isFallback !== true) {
        const normalized = normalizeReferralRewardSettings(json.settings);
        if (normalized) {
          saveVerifiedCache(normalized);
          return { settings: normalized, isFallback: false, isVerified: true };
        }
      }
    }
  } catch (apiErr) {
    // Network fallback
  }

  // 3. Tertiary: Verified Local Cache Fallback
  const verifiedCache = getVerifiedCachedReferralSettings();
  if (verifiedCache) {
    return { settings: verifiedCache, isFallback: true, isVerified: true };
  }

  return { settings: DEFAULT_REFERRAL_SETTINGS, isFallback: true, isVerified: false };
}

/**
 * Save Referral Reward Settings to public.leton_content (id = 'referral_settings') and Backend API,
 * with post-save database verification.
 * Super Admin only.
 */
export async function saveReferralRewardSettings(
  newSettings: { referrer_reward: number; referred_reward: number },
  adminRole?: string,
  adminUsername?: string
): Promise<{ success: boolean; error?: string; settings?: ReferralRewardSettings }> {
  const refReward = Number(newSettings.referrer_reward);
  const memReward = Number(newSettings.referred_reward);

  if (isNaN(refReward) || !Number.isInteger(refReward) || refReward < 0) {
    return { success: false, error: 'Reward Pengundang harus berupa bilangan bulat positif (minimal 0 poin).' };
  }
  if (isNaN(memReward) || !Number.isInteger(memReward) || memReward < 0) {
    return { success: false, error: 'Reward Member Baru harus berupa bilangan bulat positif (minimal 0 poin).' };
  }
  if (refReward > 100000 || memReward > 100000) {
    return { success: false, error: 'Nilai reward maksimal 100.000 poin untuk menjaga stabilitas sistem loyalty.' };
  }

  const adminToken = typeof window !== 'undefined'
    ? localStorage.getItem('leton_admin_token') || ''
    : '';

  const nowIso = new Date().toISOString();
  const payload: ReferralRewardSettings = {
    referrer_reward: Math.round(refReward),
    referred_reward: Math.round(memReward),
    updated_at: nowIso,
    updated_by: adminUsername || 'Super Admin',
  };

  let savedToSupabase = false;
  let lastError = '';

  // 1. Sync with Backend API (/api/admin/referral-settings) if available
  try {
    const res = await fetch(getApiUrl('/api/admin/referral-settings'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${adminToken}`,
        'x-admin-role': adminRole || 'super_admin',
      },
      body: JSON.stringify(payload),
    });

    const json = await res.json().catch(() => ({}));
    if (res.status === 403 || res.status === 401) {
      return {
        success: false,
        error: json.error || 'Akses Ditolak: Hanya Super Admin yang dapat mengubah pengaturan reward referral.',
      };
    }
    if (res.ok && json.success && json.savedToDatabase === true) {
      savedToSupabase = true;
    }
  } catch (apiErr) {
    console.warn('[ReferralSettings] API save note, ensuring direct Supabase persistence:', apiErr);
  }

  // 2. Always ensure authoritative persistence in Supabase public.leton_content (id = 'referral_settings')
  if (!savedToSupabase) {
    try {
      const client = getSupabase('super_admin');
      const { error: sbErr } = await client.from('leton_content').upsert({
        id: 'referral_settings',
        content: payload,
        updated_at: nowIso,
      });

      if (sbErr) {
        lastError = sbErr.message || 'Gagal menyimpan ke database Supabase.';
      } else {
        savedToSupabase = true;
      }
    } catch (err: any) {
      lastError = err?.message || 'Terjadi kesalahan sistem saat menyimpan ke database.';
    }
  }

  if (!savedToSupabase) {
    return { success: false, error: lastError || 'Gagal menyimpan pengaturan referral ke database.' };
  }

  // 3. Authoritative post-save read-back verification from public.leton_content
  let finalSettings: ReferralRewardSettings = payload;
  try {
    const client = getSupabase('super_admin');
    const { data: verifyRow, error: verifyErr } = await client
      .from('leton_content')
      .select('*')
      .eq('id', 'referral_settings')
      .maybeSingle();

    if (!verifyErr && verifyRow && verifyRow.content) {
      const verified = normalizeReferralRewardSettings(verifyRow.content, verifyRow.updated_at);
      if (verified) {
        if (
          verified.referrer_reward !== payload.referrer_reward ||
          verified.referred_reward !== payload.referred_reward
        ) {
          return {
            success: false,
            error: 'Verifikasi database gagal: Nilai di database tidak sesuai dengan nilai yang disimpan.',
          };
        }
        finalSettings = verified;
      }
    }
  } catch {}

  saveVerifiedCache(finalSettings);
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('leton_referral_settings_updated', { detail: finalSettings }));
  }

  return { success: true, settings: finalSettings };
}
